/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Grid,
  Image,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useTheme } from '../../contexts/ThemeProvider';
import { zodResolver } from '@hookform/resolvers/zod';
import { IProfileForm } from '@partiu/api/types';
import { z } from 'zod';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { masks } from '@partiu/ui/masks';
import { useMutation } from 'react-query';
import { removeAccount } from '@partiu/api/club/api';
import { useProfile } from '@partiu/ui/contexts/ProfileProvider';
import React from 'react';

function RemoveAccountModal() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { logout } = useProfile();
  const cancelRef: any = React.useRef();

  const removeMutation = useMutation(() => removeAccount(), {
    onSuccess() {
      logout();
    },
    onError() {
      toast({
        title: 'Não foi possível apagar seus dados.',
        description:
          'Entre em contato conosco pelo email suporte@partiu.com.br para pedir sua remoção de dados. O prazo máximo é de cinco dias úteis.',
      });
    },
  });

  return (
    <>
      <AlertDialog leastDestructiveRef={cancelRef} isOpen={isOpen} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent margin="20px">
            <AlertDialogHeader>Tem certeza?</AlertDialogHeader>
            <AlertDialogCloseButton />
            <AlertDialogBody>
              Essa ação apagará todos os dados relacionados a sua conta em nosso banco de dados, inclusive seu acesso
              aos clubes. Caso recrie sua conta, você não terá mais acesso ao Clube e deverá consultar a reativação com
              a sua empresa.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                variant="outline"
                color={'#5e5e5b'}
                backgroundColor="white"
                borderColor="primary"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button onClick={() => removeMutation.mutate()} ml={3}>
                Deletar meus Dados
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <Button
        color={'#5e5e5b'}
        borderColor="primary"
        variant="ghost"
        width={'fit-content'}
        onClick={onOpen}
        margin={'auto'}
        backgroundColor={'transparent'}
      >
        Quero apagar minha conta
      </Button>
    </>
  );
}

export function ProfileForm() {
  const { name } = useTheme();
  const { profile, updateProfile, isLoading } = useProfile();

  const formSchema = z.object({
    full_name: z.string().min(1),
    telephone: z.string().length(15),
    email: z.string().email(),
    cpf: z.string().length(14),
  });

  const methods = useForm<IProfileForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      full_name: profile?.fullName,
      telephone: profile?.telephone ? masks.cell(profile.telephone) : '',
      email: profile?.email,
      cpf: profile?.cpf ? masks.cpf(profile.cpf) : '',
    },
  });

  const { control, handleSubmit } = methods;

  function doUpdateProfile(data: IProfileForm) {
    updateProfile({ full_name: data.full_name, telephone: masks.unmask(data.telephone) });
  }

  return (
    <Grid width={'100%'} templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}>
      <Box
        display={{
          base: 'none',
          md: 'flex',
        }}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Image padding="20px" width={'70%'} marginRight="auto" src="/assets/side-image.png" />
      </Box>
      <VStack spacing={5} align={'flex-start'} width={'100%'}>
        <Text fontSize={'14px'}>
          Verifique aqui todos seus dados pessoais vinculados à {name}. Alguns campos não podem ser editados.
        </Text>
        <form onSubmit={handleSubmit(doUpdateProfile)} style={{ width: '100%' }}>
          <VStack spacing={5} w={'100%'}>
            <Input name="full_name" control={control} label="Nome Completo" />
            <Input disabled name="email" control={control} label="Email" />
            <Input disabled={!!profile?.cpf} name="cpf" mask="cpf" control={control} label="CPF" />
            <Input name="telephone" mask="cell" control={control} label="Celular" />
            <Button isLoading={isLoading} type="submit" marginTop={'10px'}>
              Atualizar
            </Button>
          </VStack>
        </form>
        <RemoveAccountModal />
      </VStack>
    </Grid>
  );
}
